import React, { useState, useEffect, useMemo } from 'react';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';

const DEFAULT_IMAGES = [
  { src: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782323364/Cinomon_spjmrs.png', bg: '#E7EEE5', textColor: '#B56535' },
  { src: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782323366/coco_vezbpb.png', bg: '#EFE6D6', textColor: '#1C1B1A' },
  { src: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1782323366/tea_iwii8n.png', bg: '#F7F3EB', textColor: '#1D3EC3' }
];

// Generate optimized Cloudinary URL with auto-quality and format
const optimizeImageUrl = (url, width) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  // Insert Cloudinary transformation: auto quality, auto format, responsive width
  return url.replace('/upload/', `/upload/f_auto,q_auto,w_${width}/`);
};

export default function Hero() {
  const { landingSettings } = useStore();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const IMAGES = useMemo(() => {
    if (!landingSettings || landingSettings.length === 0) return DEFAULT_IMAGES;
    const heroSettings = landingSettings
      .filter(s => s.section === 'Hero')
      .sort((a, b) => a.setting_key.localeCompare(b.setting_key));
    if (heroSettings.length < 3) return DEFAULT_IMAGES;
    return heroSettings.map(s => ({
      src: s.image_url,
      bg: s.bg_color,
      textColor: s.text_color
    }));
  }, [landingSettings]);

  const numItems = IMAGES.length;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Preload first image eagerly, rest lazily
  useEffect(() => {
    if (IMAGES.length > 0) {
      const img = new Image();
      img.src = optimizeImageUrl(IMAGES[0].src, 800);
    }
    // Preload adjacent images after a short delay
    const timer = setTimeout(() => {
      IMAGES.forEach((item, i) => {
        if (i > 0) {
          const preImg = new Image();
          preImg.src = optimizeImageUrl(item.src, 800);
        }
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [IMAGES]);

  const navigate = (dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex((prev) => (dir === 'next' ? (prev + 1) % numItems : (prev + numItems - 1) % numItems));
    setTimeout(() => setIsAnimating(false), 650);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isAnimating) {
        setIsAnimating(true);
        setActiveIndex((prev) => (prev + 1) % numItems);
        setTimeout(() => setIsAnimating(false), 650);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [isAnimating, numItems]);

  const currentColor = IMAGES[activeIndex].textColor;

  return (
    <section 
      className="relative w-full h-screen overflow-hidden transition-colors duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
      style={{ backgroundColor: IMAGES[activeIndex].bg, fontFamily: "'Inter', sans-serif" }}
    >
      <div 
        className="absolute inset-0 pointer-events-none z-[50]"
        style={{
          opacity: 0.4,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat'
        }}
      />

      <div className="absolute inset-x-0 flex items-center justify-center pointer-events-none select-none z-[2]" style={{ top: '22%' }}>
        <h1 
          className="uppercase whitespace-nowrap transition-colors duration-[650ms]"
          style={{
            color: currentColor,
            fontFamily: "'Anton', sans-serif",
            fontSize: 'clamp(90px, 28vw, 380px)',
            fontWeight: 900,
            opacity: 0.1,
            lineHeight: 1,
            letterSpacing: '-0.02em'
          }}
        >
          CEYLON
        </h1>
      </div>

      <div className="absolute inset-0 z-[3] pt-24">
        {IMAGES.map((item, index) => {
          const isCenter = index === activeIndex;
          const isLeft = index === (activeIndex + numItems - 1) % numItems;
          const isRight = index === (activeIndex + 1) % numItems;
          
          let transform = '';
          let blur = 'blur(0px)';
          let opacity = 1;
          let zIndex = 5;
          let left = '50%';
          let height = '100%';
          let bottom = 0;

          if (isCenter) {
            transform = `translateX(-50%) scale(${isMobile ? 1.25 : 1.68})`;
            blur = 'blur(0px)';
            opacity = 1;
            zIndex = 20;
            left = '50%';
            height = isMobile ? '60%' : '85%';
            bottom = isMobile ? '18%' : '5%';
          } else if (isLeft) {
            transform = `translateX(-50%) scale(1)`;
            blur = 'blur(2px)';
            opacity = 0.85;
            zIndex = 10;
            left = isMobile ? '20%' : '30%';
            height = isMobile ? '16%' : '25%';
            bottom = isMobile ? '25%' : '10%';
          } else if (isRight) {
            transform = `translateX(-50%) scale(1)`;
            blur = 'blur(2px)';
            opacity = 0.85;
            zIndex = 10;
            left = isMobile ? '80%' : '70%';
            height = isMobile ? '16%' : '25%';
            bottom = isMobile ? '25%' : '10%';
          }

          // Use optimized URL - higher quality for center, lower for side images
          const imgWidth = isCenter ? 800 : 400;
          const optimizedSrc = optimizeImageUrl(item.src, imgWidth);

          return (
            <div 
              key={index}
              className="absolute transition-all duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform"
              style={{
                aspectRatio: '0.6 / 1',
                transform,
                filter: blur,
                opacity,
                zIndex,
                left,
                height,
                bottom
              }}
            >
              <img 
                src={optimizedSrc} 
                alt={`Product ${index + 1}`}
                className="w-full h-full object-contain object-bottom drop-shadow-2xl"
                draggable={false}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                fetchpriority={index === 0 ? 'high' : 'auto'}
              />
            </div>
          );
        })}
      </div>

      <div className="absolute bottom-8 left-6 sm:bottom-16 sm:left-12 lg:left-24 z-[60] max-w-[360px]">
        <h2 
          className="opacity-95 font-serif font-bold uppercase mb-3 sm:mb-4 text-2xl sm:text-4xl leading-[1.1] transition-colors duration-[650ms]"
          style={{ letterSpacing: '0.02em', color: currentColor }}
        >
          PURE CEYLON NATURE
        </h2>
        <p 
          className="hidden sm:block opacity-85 text-xs sm:text-sm mb-6 leading-[1.6] transition-colors duration-[650ms]"
          style={{ color: currentColor }}
        >
          Authentic Ceylon spices, herbal teas, cold-pressed virgin coconut oil, and other value-added products, crafted from nature's finest ingredients.
        </p>
        
        <div className="flex gap-4">
          <button 
            onClick={() => navigate('prev')}
            className="flex items-center justify-center rounded-full border w-12 h-12 sm:w-14 sm:h-14 transition-all duration-300 ease-out hover:scale-110 active:scale-95 backdrop-blur-sm"
            style={{ color: currentColor, borderColor: currentColor }}
            aria-label="Previous"
          >
            <LuArrowLeft size={24} strokeWidth={2} />
          </button>
          <button 
            onClick={() => navigate('next')}
            className="flex items-center justify-center rounded-full border w-12 h-12 sm:w-14 sm:h-14 transition-all duration-300 ease-out hover:scale-110 active:scale-95 backdrop-blur-sm"
            style={{ color: currentColor, borderColor: currentColor }}
            aria-label="Next"
          >
            <LuArrowRight size={24} strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="absolute bottom-8 right-6 sm:bottom-16 sm:right-12 lg:right-24 z-[60]">
        <Link 
          to="/shop"
          className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-colors duration-[650ms] uppercase group"
          style={{
            color: currentColor,
            fontFamily: "'Anton', sans-serif",
            fontSize: 'clamp(24px, 4vw, 48px)',
            letterSpacing: '0.02em',
            lineHeight: 1,
            textDecoration: 'none'
          }}
        >
          SHOP COLLECTION
          <LuArrowRight className="w-6 h-6 sm:w-10 sm:h-10 transform group-hover:translate-x-2 transition-transform duration-300" strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}